import React, { useState, useEffect } from 'react';
import { Play, Square, RefreshCw, Download, Clock, CheckCircle, XCircle } from 'lucide-react';
import { dashboardApi } from '../services/api';

const HeepsyScraper = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startPage, setStartPage] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSessions();

    // Poll for updates if there's an active session
    const interval = setInterval(() => {
      if (currentSession?.session?.status === 'running') {
        loadSessionStatus(currentSession.session.id);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [currentSession]);

  const loadSessions = async () => {
    try {
      const response = await dashboardApi.getHeepsySessions(10);
      setSessions(response.data.sessions);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };

  const loadSessionStatus = async (sessionId) => {
    try {
      const response = await dashboardApi.getHeepsyStatus(sessionId);
      setCurrentSession(response.data);
    } catch (err) {
      console.error('Failed to load session status:', err);
    }
  };

  const handleStart = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await dashboardApi.startHeepsyScraper(startPage);

      if (response.data.success) {
        // Start monitoring the session
        setTimeout(() => {
          loadSessionStatus(response.data.sessionId);
          loadSessions();
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start scraper');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!currentSession?.session?.id) return;

    try {
      await dashboardApi.stopHeepsyScraper(currentSession.session.id);
      setTimeout(() => {
        loadSessionStatus(currentSession.session.id);
        loadSessions();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to stop scraper');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'stopped':
        return <Square className="w-5 h-5 text-gray-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'stopped':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Heepsy Scraper</h2>

      {/* Control Panel */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Start New Scraping Session</h3>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Page
            </label>
            <input
              type="number"
              min="1"
              value={startPage}
              onChange={(e) => setStartPage(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={currentSession?.isRunning}
            />
          </div>

          <button
            onClick={handleStart}
            disabled={loading || currentSession?.isRunning}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            {loading ? 'Starting...' : 'Start Scraping'}
          </button>

          {currentSession?.isRunning && (
            <button
              onClick={handleStop}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <Square className="w-4 h-4" />
              Stop
            </button>
          )}
        </div>

        {/* Current Session Status */}
        {currentSession && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-lg font-semibold mb-4">Current Session</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusIcon(currentSession.session.status)}
                  <span className={`px-2 py-1 rounded text-sm font-medium ${getStatusColor(currentSession.session.status)}`}>
                    {currentSession.session.status}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Current Page</p>
                <p className="text-lg font-semibold mt-1">{currentSession.currentPage || currentSession.session.endPage || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Scraped</p>
                <p className="text-lg font-semibold mt-1">{currentSession.session.totalScraped?.toLocaleString() || '0'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Started</p>
                <p className="text-sm mt-1">
                  {currentSession.session.startedAt
                    ? new Date(currentSession.session.startedAt).toLocaleString()
                    : '-'}
                </p>
              </div>
            </div>

            {currentSession.isRunning && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                  <span>Scraping in progress...</span>
                  <span>Page {currentSession.currentPage}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full animate-pulse"
                    style={{ width: '100%' }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Session History */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold">Recent Sessions</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pages
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Scraped
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{session.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(session.status)}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(session.status)}`}>
                        {session.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.start_page} - {session.end_page || '...'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.total_profiles_scraped?.toLocaleString() || '0'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(session.started_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {session.completed_at ? new Date(session.completed_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => loadSessionStatus(session.id)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {sessions.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No scraping sessions yet. Start your first session above!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeepsyScraper;
